import React, { useState, useCallback, useEffect } from 'react';
import { cn } from '@ui/atoms/utils/utils';
import Image from '@ui/atoms/Image';
import { getPaginationItems } from '@ui/atoms/utils/helper';

const Pagination = React.forwardRef(
    (
        {
            className = '', // additional classes
            pageSize = 10, // number of rows per page
            totalCount, // total number of rows in the table
            skipSize = 2, // number of pages to skip when clicking on ellipsis
            hideOnSinglePage = false, // hide pagination if pageCount is less than or equal to 1
            onChange, // function to handle page change event (page, pageSize) => {}
            tableRef, // table ref to scroll to top of the table on page change
            offset = 0, // offset from top of the page
            defaultPage = 1, // default page to show
            ...props
        },
        ref
    ) => {
        const [currentPage, setCurrentPage] = useState(
            defaultPage ? defaultPage : 1
        );

        useEffect(() => {
            setCurrentPage(defaultPage);
        }, [defaultPage]);

        const [hoveredEllipsis, setHoveredEllipsis] = useState(null);

        const pageCount = Math.ceil(totalCount / pageSize);
        const paginationItems = getPaginationItems(currentPage, pageCount);

        const handlePageChange = useCallback(
            (page) => {
                setCurrentPage(page);
                if (onChange) {
                    onChange(page, pageSize);
                }
            },
            [onChange, pageSize]
        );


        const handleEllipsisClick = useCallback(
            (page, index) => {
                const newPage = page.endsWith('1') ? (paginationItems[index+1] - skipSize) : (paginationItems[index-1] + skipSize);
                handlePageChange(newPage);
            },
            [currentPage, skipSize, pageCount, handlePageChange]
        );

        const startItem = (currentPage - 1) * pageSize + 1;
        const endItem = Math.min(currentPage * pageSize, totalCount);

        if (hideOnSinglePage && pageCount <= 1) return null;

        return (
            <div className="flex flex-col-reverse items-center justify-between w-full gap-4 md:flex-row">
                <div className="text-gray-700 p4-regular">
                    {startItem}-{endItem} of {totalCount} results
                </div>
                <div
                    ref={ref}
                    className={`relative flex items-center justify-center gap-1 ${className}`}
                    {...props}
                >
                    <PaginationButton
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        ariaLabel="Previous Page"
                    >
                        <Image
                            src={`/icons/ChevronLeft.svg`}
                            className="w-4 h-4"
                            alt="Previous"
                        />
                    </PaginationButton>
                    {paginationItems.map((page, index) => (
                        <div
                            key={index}
                            className={cn(
                                'flex items-center transition-all ease-in-out justify-center w-8 h-8 text-gray-700 rounded cursor-pointer hover:bg-gray-200 p4-regular',
                                {
                                    'bg-primary-50 hover:bg-primary-70 text-primary-500 p4-medium':
                                        currentPage === page,
                                }
                            )}
                            onMouseEnter={() => {
                                if (
                                    typeof page === 'string' &&
                                    page.includes('...')
                                ) {
                                    setHoveredEllipsis(page);
                                }
                            }}
                            onMouseLeave={() => setHoveredEllipsis(null)}
                            onClick={() =>
                                typeof page === 'number'
                                    ? handlePageChange(page)
                                    : handleEllipsisClick(page, index)
                            }
                        >
                            {typeof page === 'string' &&
                            page.includes('...') ? (
                                hoveredEllipsis === page ? (
                                    <Image
                                        src={`/icons/ChevronLeftDisabled.svg`}
                                        className={`w-4 h-4 ${
                                            page.endsWith('1')
                                                ? ''
                                                : 'rotate-180'
                                        }`}
                                        alt="Ellipsis"
                                    />
                                ) : (
                                    '...'
                                )
                            ) : (
                                page
                            )}
                        </div>
                    ))}
                    <PaginationButton
                        disabled={currentPage === pageCount}
                        onClick={() => handlePageChange(currentPage + 1)}
                        ariaLabel="Next Page"
                    >
                        <Image
                            src={`/icons/ChevronLeft.svg`}
                            className="w-4 h-4 rotate-180"
                            alt="Next"
                        />
                    </PaginationButton>
                </div>
            </div>
        );
    }
);

 const PaginationButton = ({ disabled, onClick, children, ariaLabel }) => (
     <button
         className={cn(
             'flex items-center justify-center w-8 h-8 rounded cursor-pointer hover:bg-gray-200',
             {
                 'opacity-50 cursor-not-allowed hover:bg-transparent': disabled,
             }
         )}
         onClick={!disabled ? onClick : undefined}
         aria-label={ariaLabel}
     >
         {children}
     </button>
 );
 export { Pagination, PaginationButton };
